import { useEffect, useMemo, useState } from "react";
import type { Condition, ExperimentState, Story } from "../types";

type FormValue = string | number;

function RadioScale({
  name,
  value,
  onChange,
  low = "まったくそう思わない",
  high = "非常にそう思う",
  min = 1,
  max = 7,
}: {
  name: string;
  value?: FormValue;
  onChange: (value: number) => void;
  low?: string;
  high?: string;
  min?: number;
  max?: number;
}) {
  const values = Array.from({ length: max - min + 1 }, (_, index) => min + index);
  return (
    <div className="scale-field">
      <span>{low}</span>
      <div className="scale-options">
        {values.map((item) => (
          <label key={item}>
            <input
              type="radio"
              name={name}
              value={item}
              checked={Number(value) === item}
              onChange={() => onChange(item)}
            />
            <b>{item}</b>
          </label>
        ))}
      </div>
      <span>{high}</span>
    </div>
  );
}

export function PreQuestionnaire({
  initial,
  guideVisible,
  onGuideChange,
  onSubmit,
}: {
  initial: Record<string, string>;
  guideVisible: boolean;
  onGuideChange: (value: boolean) => void;
  onSubmit: (answers: Record<string, string>) => void;
}) {
  const [answers, setAnswers] = useState(initial);
  const set = (key: string, value: string) => setAnswers((current) => ({ ...current, [key]: value }));
  const ready = Boolean(answers.age && answers.reading && answers.mapExperience);

  return (
    <main className="form-page">
      <section className="form-hero">
        <p className="eyebrow">MAIN EXPERIMENT · TEST BUILD</p>
        <h1>事前質問</h1>
        <p>紙面での研究説明と参加確認が済んでいることを前提に、ここから開始します。</p>
      </section>

      <form className="paper-form" onSubmit={(event) => { event.preventDefault(); onSubmit(answers); }}>
        <div className="test-setting">
          <div>
            <strong>テスト環境の表示設定</strong>
            <p>読書位置の判定線を表示するか選べます。本番では実験者が事前に固定します。</p>
          </div>
          <label className="switch-row">
            <input type="checkbox" checked={guideVisible} onChange={(event) => onGuideChange(event.target.checked)} />
            <span>{guideVisible ? "案内線を表示" : "案内線を非表示"}</span>
          </label>
        </div>

        <fieldset>
          <legend>1　年齢について</legend>
          <div className="choice-list">
            {["18～24歳", "25～34歳", "35歳以上", "回答しない"].map((item) => (
              <label key={item}><input type="radio" name="age" checked={answers.age === item} onChange={() => set("age", item)} />{item}</label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>2　普段、物語や小説をどの程度読みますか</legend>
          <div className="choice-list">
            {["週に数回以上", "月に数回", "年に数回", "ほとんど読まない", "回答しない"].map((item) => (
              <label key={item}><input type="radio" name="reading" checked={answers.reading === item} onChange={() => set("reading", item)} />{item}</label>
            ))}
          </div>
        </fieldset>
        <fieldset>
          <legend>3　人物相関図を見た経験はありますか</legend>
          <div className="choice-list">
            {["よくある", "ときどきある", "ほとんどない", "ない", "覚えていない", "回答しない"].map((item) => (
              <label key={item}><input type="radio" name="mapExperience" checked={answers.mapExperience === item} onChange={() => set("mapExperience", item)} />{item}</label>
            ))}
          </div>
        </fieldset>
        <div className="page-actions">
          <span>必須項目はプロトタイプ確認用です。</span>
          <button className="primary-button" type="submit" disabled={!ready}>回答を保存して操作練習へ</button>
        </div>
      </form>
    </main>
  );
}

export function PracticeScreen({ onContinue }: { onContinue: () => void }) {
  const [tab, setTab] = useState<"text" | "map">("text");
  return (
    <main className="experiment-shell narrow-shell">
      <p className="eyebrow">PRACTICE</p>
      <h1>操作練習</h1>
      <p className="lead">実験では「本文」と「人物相関図」を自由に切り替えます。相関図は、本文で到達した位置までの情報だけが更新されます。</p>
      <div className="practice-card">
        <div className="tab-bar" role="tablist">
          <button type="button" className={tab === "text" ? "active" : ""} onClick={() => setTab("text")}>本文</button>
          <button type="button" className={tab === "map" ? "active" : ""} onClick={() => setTab("map")}>人物相関図</button>
        </div>
        {tab === "text" ? (
          <div className="practice-content"><p>窓の外で鐘が鳴り、春子は駅へ向かった。本文はこのように表示されます。実験では本文領域をスクロールします。</p></div>
        ) : (
          <div className="practice-map"><span>春子</span><i /><span>駅員</span><small>知り合い</small></div>
        )}
      </div>
      <ul className="instruction-list">
        <li>本文中の到達位置に応じて、人物と関係が段階的に追加・更新されます。</li>
        <li>上へ読み返しても、一度表示された相関図情報は消えません。</li>
        <li>相関図では人物や関係を選択して、表示可能な詳細を確認できます。</li>
      </ul>
      <div className="page-actions"><span>両方のタブを確認してから進んでください。</span><button className="primary-button" type="button" onClick={onContinue}>練習を終えて試行へ</button></div>
    </main>
  );
}

export function BlockIntro({ state, condition, onContinue }: { state: ExperimentState; condition: Condition; onContinue: () => void }) {
  return (
    <main className="center-page">
      <section className="intro-card">
        <p className="eyebrow">STORY {state.blockNumber + 1} / 3</p>
        <h1>これから文章を読みます</h1>
        <p>必要に応じて人物相関図を何度でも確認できます。速さを競う実験ではありません。途中で2分間の休憩が入ります。</p>
        <dl className="intro-list"><div><dt>今回の段階</dt><dd>{state.blockNumber + 1}つ目の試行</dd></div><div className="test-condition-row"><dt>テスト条件</dt><dd>{condition}（{condition === "H" ? "全情報オン" : condition === "P" ? "主要情報" : "基本表示"}）</dd></div><div><dt>操作</dt><dd>本文と人物相関図を切り替え</dd></div><div><dt>中断</dt><dd>別課題を行わない2分間の休憩</dd></div></dl>
        <button className="primary-button wide" type="button" onClick={onContinue}>読書を始める</button>
      </section>
    </main>
  );
}

export function BreakScreen({ startedAt, onComplete }: { startedAt: string; onComplete: () => void }) {
  const finishAt = useMemo(() => new Date(startedAt).getTime() + 120_000, [startedAt]);
  const [remaining, setRemaining] = useState(() => Math.max(0, finishAt - Date.now()));
  useEffect(() => {
    const id = window.setInterval(() => setRemaining(Math.max(0, finishAt - Date.now())), 250);
    return () => window.clearInterval(id);
  }, [finishAt]);
  const seconds = Math.ceil(remaining / 1000);

  return (
    <main className="break-page">
      <section className="break-card">
        <p className="eyebrow">READING BREAK</p>
        <h1>2分間休憩してください</h1>
        <p>本文と人物相関図は休憩中は表示されません。別の課題はありません。画面から目を離し、楽な姿勢でお待ちください。</p>
        <div className="timer" aria-live="polite"><strong>{String(Math.floor(seconds / 60)).padStart(2, "0")}</strong><span>:</span><strong>{String(seconds % 60).padStart(2, "0")}</strong></div>
        <button className="primary-button wide" type="button" disabled={remaining > 0} onClick={onComplete}>休憩を終えて読書を再開</button>
        <button className="test-skip" type="button" onClick={onComplete}>テスト用　待ち時間を省略</button>
      </section>
    </main>
  );
}

export function ComprehensionScreen({ story, onSubmit }: { story: Story; onSubmit: (answers: Record<string, string>) => void }) {
  const [answers, setAnswers] = useState<Record<string, string>>({});
  const set = (key: string, value: string) => setAnswers((current) => ({ ...current, [key]: value }));
  const questions = [
    { id: "q1", text: "灯台の青い光は、何のために使われていましたか", options: ["祭りを始める合図", "記録を守るための警告", "船の出航許可", "わからない"] },
    { id: "q2", text: "三浦澪と榊灯子の関係として明らかになったものはどれですか", options: ["元同僚", "いとこ", "母を同じくする姉妹", "わからない"] },
  ];
  return (
    <main className="form-page compact-form">
      <section className="form-hero"><p className="eyebrow">COMPREHENSION</p><h1>内容確認</h1><p>本文と人物相関図は参照せずに回答してください。分からない場合は「わからない」を選べます。</p></section>
      <form className="paper-form" onSubmit={(event) => { event.preventDefault(); onSubmit(answers); }}>
        {questions.map((question, index) => (
          <fieldset key={question.id}><legend>{index + 1}　{question.text}</legend><div className="choice-list">{question.options.map((item) => <label key={item}><input type="radio" name={question.id} checked={answers[question.id] === item} onChange={() => set(question.id, item)} />{item}</label>)}</div></fieldset>
        ))}
        <div className="page-actions"><span>この課題は能力評価を目的とするものではありません。</span><button className="primary-button" type="submit" disabled={!answers.q1 || !answers.q2}>回答を保存して評価へ</button></div>
      </form>
    </main>
  );
}

export function ConditionEvaluation({ onSubmit }: { onSubmit: (answers: Record<string, FormValue>) => void }) {
  const nasa = ["精神的要求", "身体的要求", "時間的要求", "作業達成度", "努力", "不満"];
  const [answers, setAnswers] = useState<Record<string, FormValue>>(() =>
    Object.fromEntries(nasa.map((item) => [item, 50])),
  );
  const set = (key: string, value: FormValue) => setAnswers((current) => ({ ...current, [key]: value }));
  const ready = ["amount", "helpful", ...nasa].every((key) => answers[key] !== undefined);
  return (
    <main className="form-page">
      <section className="form-hero"><p className="eyebrow">CONDITION REVIEW</p><h1>今回の人物相関図について</h1><p>正解はありません。実際に使ったときの印象に最も近いものを選んでください。</p></section>
      <form className="paper-form" onSubmit={(event) => { event.preventDefault(); onSubmit(answers); }}>
        <fieldset><legend>表示された情報量は、読書に使うものとしてどの程度でしたか</legend><RadioScale name="amount" value={answers.amount} onChange={(value) => set("amount", value)} low="かなり少なすぎた" high="かなり多すぎた" /></fieldset>
        <fieldset><legend>全体として、この人物相関図は今回の物語を読むことに役立った</legend><RadioScale name="helpful" value={answers.helpful} onChange={(value) => set("helpful", value)} /></fieldset>
        <fieldset><legend>作業負担　0～100</legend><p className="field-note">正式版では採用する日本語版Raw NASA-TLXの確定文言へ差し替えます。</p>{nasa.map((item) => <label className="range-row" key={item}><span>{item}</span><input type="range" min="0" max="100" step="5" value={Number(answers[item] ?? 50)} onChange={(event) => set(item, Number(event.target.value))} /><output>{Number(answers[item] ?? 50)}</output></label>)}</fieldset>
        <fieldset><legend>任意の自由記述</legend><p className="field-note">本人や第三者の氏名・連絡先・機密情報は記入しないでください。</p><textarea value={String(answers.comment ?? "")} onChange={(event) => set("comment", event.target.value)} placeholder="足りなかった情報、多すぎた情報、分かりにくかった点など" /></fieldset>
        <div className="page-actions"><span>回答は試行ごとに保存されます。</span><button className="primary-button" type="submit" disabled={!ready}>評価を保存して次へ</button></div>
      </form>
    </main>
  );
}

export function FinalEvaluation({ onSubmit }: { onSubmit: (answers: Record<string, FormValue>) => void }) {
  const [answers, setAnswers] = useState<Record<string, FormValue>>({});
  const set = (key: string, value: FormValue) => setAnswers((current) => ({ ...current, [key]: value }));
  return (
    <main className="form-page">
      <section className="form-hero"><p className="eyebrow">FINAL REVIEW</p><h1>終了時評価</h1><p>3回の試行全体を振り返って回答してください。</p></section>
      <form className="paper-form" onSubmit={(event) => { event.preventDefault(); onSubmit(answers); }}>
        <fieldset><legend>このシステムを頻繁に使ってみたいと思う</legend><RadioScale name="sus1" value={answers.sus1} onChange={(value) => set("sus1", value)} min={1} max={5} /></fieldset>
        <fieldset><legend>このシステムは不必要に複雑だと思う</legend><RadioScale name="sus2" value={answers.sus2} onChange={(value) => set("sus2", value)} min={1} max={5} /></fieldset>
        <fieldset><legend>物語を中断した後に最も使いたい表示</legend><div className="choice-list">{["表示1", "表示2", "表示3", "判断できない"].map((item) => <label key={item}><input type="radio" name="preferred" checked={answers.preferred === item} onChange={() => set("preferred", item)} />{item}</label>)}</div></fieldset>
        <fieldset><legend>人物相関図を開いた理由、またはあまり開かなかった理由</legend><textarea value={String(answers.reason ?? "")} onChange={(event) => set("reason", event.target.value)} placeholder="任意回答" /></fieldset>
        <div className="page-actions"><span>SUS全10項目などの確定質問は後から追加します。</span><button className="primary-button" type="submit" disabled={!answers.sus1 || !answers.sus2 || !answers.preferred}>回答を保存して実験を終了</button></div>
      </form>
    </main>
  );
}

export function CompleteScreen({ state, onReset }: { state: ExperimentState; onReset: () => void }) {
  const completedNormally = state.trials.length === 3 && !state.stoppedAt;
  return (
    <main className="center-page">
      <section className="complete-card">
        <div className="complete-check" aria-hidden="true">✓</div>
        <p className="eyebrow">SESSION COMPLETE</p>
        <h1>{completedNormally ? "すべての試行が終了しました" : "実験を中止しました"}</h1>
        <p>{completedNormally ? "保存状態を確認し、実験者へこの画面をお知らせください。" : "これ以上の操作は必要ありません。実験者へこの画面をお知らせください。"}</p>
        <div className="completion-code"><span>テスト用実験ID</span><strong>{state.participantCode}</strong></div>
        <button className="secondary-button" type="button" onClick={onReset}>テストデータを消去して最初から試す</button>
      </section>
    </main>
  );
}
